import * as React from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  Banknote,
  ChevronLeft,
  ChevronRight,
  Clock,
  MoreHorizontal,
  Pencil,
  ReceiptText,
  RefreshCw,
  Repeat,
  Search,
  Sparkles,
  Trash2,
  TrendingDown,
  TrendingUp,
  Users,
  Wallet,
  X,
} from "lucide-react";
import {
  AreaChart,
  Badge,
  BarChart,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTablePagination,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  DonutChart,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  StatCard,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { ExpenseEditorSheet } from "@/components/accounting/ExpenseEditorSheet";
import { RecurringExpenseEditorSheet } from "@/components/accounting/RecurringExpenseEditorSheet";
import { useCanManageAccounting } from "@/hooks/useAccountingAuthoring";
import { useEnabledModules } from "@/hooks/useEnabledModules";
import { useTeamPayAccess } from "@/hooks/useTeamPayAccess";
import { useTeamPaySummary } from "@/lib/teamPay/api";
import { usePageSizePreference } from "@/hooks/usePageSizePreference";
import { useTerms } from "@/hooks/useTerms";
import {
  EXPENSE_CATEGORY_LABELS,
  RECURRING_CADENCE_LABELS,
  RECURRING_STATUS_LABELS,
  expenseCategoryLabel,
  formatPaise,
  formatPaiseCompact,
  useDeleteExpense,
  useExplainPeriod,
  useExpenses,
  useProfitLoss,
  useProfitLossSeries,
  useRecurringExpenseAction,
  useRecurringExpenses,
  useStaffPayees,
  type Expense,
  type ExpenseCategory,
  type RecurringExpense,
  type RecurringExpenseStatus,
} from "@/lib/accounting/api";

/**
 * Phase SC-2b — IA owner-finances (accounting) workspace.
 *
 * Study centres are owner-operated businesses; this surface answers the
 * one question the owner cares about: "am I making money?". Three tabs:
 *
 *   Overview — the monthly P&L: fee income − staff cost − expenses →
 *              gross/net profit, plus a category breakdown donut.
 *   Expenses — one-time costs and months posted by a schedule.
 *   Recurring — water, electricity, rent, and any custom cost that repeats.
 *   Staff    — the unified staff-cost ledger (portal + non-portal).
 *
 * Reads gate on `view_accounting`; write affordances on `manage_accounting`
 * (admin-ceiling by default). The route is additionally wrapped in
 * `<RouteModuleGate module="accounting">` in `App.tsx`, and the BE
 * re-checks module + permission on every call.
 */

const CATEGORY_COLORS = [
  "var(--chart-1, #6366f1)",
  "var(--chart-2, #10b981)",
  "var(--chart-3, #f59e0b)",
  "var(--chart-4, #ef4444)",
  "var(--chart-5, #06b6d4)",
  "#8b5cf6",
  "#ec4899",
  "#84cc16",
];

function monthLabel(year: number, month: number): string {
  return new Date(year, month - 1, 1).toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
  });
}

export function AccountingPage() {
  const { canViewAccounting, canManageAccounting, missingView } = useCanManageAccounting();
  const { ct } = useTerms();

  const now = new Date();
  const [period, setPeriod] = React.useState({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  });

  const shiftMonth = (delta: number) => {
    setPeriod((prev) => {
      const d = new Date(prev.year, prev.month - 1 + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() + 1 };
    });
  };
  const isCurrentMonth = period.year === now.getFullYear() && period.month === now.getMonth() + 1;

  if (missingView) {
    return (
      <div className="space-y-6">
        <AccountingHeader />
        <Card className="border-amber-300/60">
          <CardHeader>
            <CardTitle className="text-base">Restricted area</CardTitle>
            <CardDescription>
              Owner finances are visible to administrators only. Ask an admin to grant the{" "}
              <span className="font-medium">View accounting</span> permission if you need access.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AccountingHeader
        right={
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="size-8"
              aria-label="Previous month"
              onClick={() => shiftMonth(-1)}
            >
              <ChevronLeft className="size-4" />
            </Button>
            <span className="min-w-[8.5rem] text-center text-sm font-medium tabular-nums">
              {monthLabel(period.year, period.month)}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="size-8"
              aria-label="Next month"
              disabled={isCurrentMonth}
              onClick={() => shiftMonth(1)}
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
        }
      />

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
          <TabsTrigger value="expenses">Expenses</TabsTrigger>
          <TabsTrigger value="recurring">Recurring costs</TabsTrigger>
          <TabsTrigger value="staff">{ct("Staff costs")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-5">
          <OverviewTab year={period.year} month={period.month} />
        </TabsContent>
        <TabsContent value="reports" className="mt-5">
          <ReportsTab year={period.year} month={period.month} />
        </TabsContent>
        <TabsContent value="expenses" className="mt-5">
          <ExpensesTab
            canManage={canManageAccounting}
            enabled={canViewAccounting}
            lockedMonth={period}
          />
        </TabsContent>
        <TabsContent value="recurring" className="mt-5">
          <RecurringTab canManage={canManageAccounting} enabled={canViewAccounting} />
        </TabsContent>
        <TabsContent value="staff" className="mt-5">
          <TeamCostTab year={period.year} month={period.month} enabled={canViewAccounting} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AccountingHeader({ right }: { right?: React.ReactNode }) {
  return (
    <PageHeader>
      <PageHeaderTop>
        <PageHeaderTitle>
          <PageHeaderHeading>Accounting</PageHeaderHeading>
          <PageHeaderDescription>
            Track one-time and recurring costs, then see gross and net profit each month.
          </PageHeaderDescription>
        </PageHeaderTitle>
        {right ? <PageHeaderActions>{right}</PageHeaderActions> : null}
      </PageHeaderTop>
    </PageHeader>
  );
}

// ── Overview (P&L) ─────────────────────────────────────────────────────

function OverviewTab({ year, month }: { year: number; month: number }) {
  const { data, isLoading, isError, refetch } = useProfitLoss(year, month);
  const { ct } = useTerms();

  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="border-border bg-card h-[116px] animate-pulse rounded-xl border"
          />
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div
        role="alert"
        className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-sm"
      >
        <AlertCircle aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
        <span className="flex-1">Couldn&apos;t load the profit &amp; loss report.</span>
        <Button variant="ghost" size="sm" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const netPositive = data.net_profit_paise >= 0;
  const grossPositive = data.gross_profit_paise >= 0;

  const segments = data.expense_breakdown.map((row, i) => ({
    name: expenseCategoryLabel(row.category, row.custom_category_label),
    value: row.amount_paise,
    color: CATEGORY_COLORS[i % CATEGORY_COLORS.length] ?? "#6366f1",
  }));

  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Fee income"
          value={formatPaiseCompact(data.fee_income_paise)}
          caption="Active monthly fee plans"
          icon={<Wallet />}
          tone="blue"
        />
        <StatCard
          label={ct("Staff cost")}
          value={formatPaiseCompact(data.staff_cost_paise)}
          caption="Active monthly wages + salaries"
          icon={<Users />}
          tone="violet"
        />
        <StatCard
          label="Gross profit"
          value={formatPaiseCompact(data.gross_profit_paise)}
          caption={ct("Fee income − staff cost")}
          icon={grossPositive ? <TrendingUp /> : <TrendingDown />}
          tone={grossPositive ? "green" : "rose"}
        />
        <StatCard
          label="Net profit"
          value={formatPaiseCompact(data.net_profit_paise)}
          caption="Gross profit − expenses"
          icon={netPositive ? <TrendingUp /> : <TrendingDown />}
          tone={netPositive ? "green" : "rose"}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-5">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Expenses this month</CardTitle>
            <CardDescription>
              {formatPaise(data.expenses_paise)} across all categories
            </CardDescription>
          </CardHeader>
          <CardContent>
            {segments.length > 0 ? (
              <DonutChart
                segments={segments}
                centerValue={formatPaiseCompact(data.expenses_paise)}
                centerLabel="Total"
              />
            ) : (
              <p className="text-muted-foreground py-8 text-center text-sm">
                No expenses logged for {monthLabel(year, month)}.
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle className="text-base">
              Profit &amp; loss — {monthLabel(year, month)}
            </CardTitle>
            <CardDescription>
              How the month&apos;s income breaks down into gross and net profit.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-1">
            <PnlRow label="Fee income" value={formatPaise(data.fee_income_paise)} />
            <PnlRow
              label={ct("Staff cost")}
              value={`− ${formatPaise(data.staff_cost_paise)}`}
              muted
            />
            <PnlRow
              label="Gross profit"
              value={formatPaise(data.gross_profit_paise)}
              emphasis
              positive={grossPositive}
            />
            <PnlRow
              label="Operating expenses"
              value={`− ${formatPaise(data.expenses_paise)}`}
              muted
            />
            <PnlRow
              label="This month only"
              value={`− ${formatPaise(data.one_time_expenses_paise ?? 0)}`}
              muted
            />
            <PnlRow
              label="Recurring bills"
              value={`− ${formatPaise(data.scheduled_expenses_paise ?? 0)}`}
              muted
            />
            <div className="border-border/60 my-1 border-t" />
            <PnlRow
              label="Net profit"
              value={formatPaise(data.net_profit_paise)}
              emphasis
              positive={netPositive}
            />
          </CardContent>
        </Card>
      </div>
      <ExplainCard year={year} month={month} />
    </div>
  );
}

function PnlRow({
  label,
  value,
  muted,
  emphasis,
  positive,
}: {
  label: string;
  value: string;
  muted?: boolean;
  emphasis?: boolean;
  positive?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between py-1.5 text-sm",
        emphasis && "font-semibold",
      )}
    >
      <span className={cn(muted && "text-muted-foreground")}>{label}</span>
      <span
        className={cn(
          "tabular-nums",
          emphasis && positive === false && "text-rose-600 dark:text-rose-400",
          emphasis && positive === true && "text-emerald-600 dark:text-emerald-400",
          muted && "text-muted-foreground",
        )}
      >
        {value}
      </span>
    </div>
  );
}

// ── Reports ────────────────────────────────────────────────────────────

function ExplainCard({ year, month }: { year: number; month: number }) {
  const explain = useExplainPeriod();
  return (
    <Card>
      <CardHeader className="gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <CardTitle className="text-base">What the books say</CardTitle>
          <CardDescription>
            A plain-language read of {monthLabel(year, month)}, including one-off costs.
          </CardDescription>
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={explain.isPending}
          onClick={() => explain.mutate({ year, month })}
        >
          <Sparkles aria-hidden="true" className="size-4" />
          {explain.isPending ? "Reading the books…" : "Explain this month"}
        </Button>
      </CardHeader>
      {explain.data ? (
        <CardContent className="space-y-2">
          <p className="text-sm font-medium">{explain.data.headline}</p>
          {explain.data.paragraphs.map((paragraph) => (
            <p key={paragraph} className="text-muted-foreground text-sm">
              {paragraph}
            </p>
          ))}
        </CardContent>
      ) : null}
      {explain.isError ? (
        <CardContent>
          <p className="text-destructive text-sm">
            Couldn&apos;t explain this month. Please retry.
          </p>
        </CardContent>
      ) : null}
    </Card>
  );
}

function ReportsTab({ year, month }: { year: number; month: number }) {
  const { data, isLoading, isError, refetch } = useProfitLossSeries(year, month, 6);
  const points = data?.months ?? [];
  const trend = points.map((point) => ({
    label: point.label.slice(2),
    Income: Math.round(point.fee_income_paise / 100),
    Expenses: Math.round(point.expenses_paise / 100),
    Net: Math.round(point.net_profit_paise / 100),
  }));
  const mix = points.map((point) => ({
    label: point.label.slice(2),
    "This month only": Math.round(point.one_time_expenses_paise / 100),
    Recurring: Math.round(point.scheduled_expenses_paise / 100),
  }));

  if (isLoading) {
    return <div className="bg-card h-64 animate-pulse rounded-xl border" />;
  }
  if (isError) {
    return <LoadError onRetry={() => refetch()} label="financial reports" />;
  }

  return (
    <div className="space-y-4">
      <ExplainCard year={year} month={month} />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Six-month profit trend</CardTitle>
            <CardDescription>
              Fee income, operating costs, and net profit in rupees.
            </CardDescription>
          </CardHeader>
          <CardContent className="h-72">
            <AreaChart
              data={trend}
              xKey="label"
              height={260}
              series={[
                { dataKey: "Income", name: "Fee income", color: "var(--chart-2, #10b981)" },
                { dataKey: "Expenses", name: "Expenses", color: "var(--chart-4, #ef4444)" },
                { dataKey: "Net", name: "Net profit", color: "var(--chart-1, #6366f1)" },
              ]}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">One-off versus recurring</CardTitle>
            <CardDescription>
              Renovation and casual labour stay in the month they were added. Recurring bills
              continue until you stop them.
            </CardDescription>
          </CardHeader>
          <CardContent className="h-72">
            <BarChart
              data={mix}
              xKey="label"
              height={260}
              series={[
                {
                  dataKey: "This month only",
                  name: "This month only",
                  color: "var(--chart-3, #f59e0b)",
                },
                { dataKey: "Recurring", name: "Recurring", color: "var(--chart-5, #06b6d4)" },
              ]}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ── Expenses tab ───────────────────────────────────────────────────────

type ExpenseDialog = { kind: "none" } | { kind: "create" } | { kind: "edit"; expense: Expense };

const CATEGORY_FILTER_ANY = "__any__";

function ExpensesTab({
  canManage,
  enabled,
  lockedMonth,
}: {
  canManage: boolean;
  enabled: boolean;
  lockedMonth: { year: number; month: number };
}) {
  const [qInput, setQInput] = React.useState("");
  const [categoryFilter, setCategoryFilter] = React.useState<string>(CATEGORY_FILTER_ANY);
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = usePageSizePreference({
    scope: "accounting-expenses",
    defaultPageSize: 10,
  });
  const [dialog, setDialog] = React.useState<ExpenseDialog>({ kind: "none" });
  const deleteMutation = useDeleteExpense();

  const query = useExpenses({
    category:
      categoryFilter === CATEGORY_FILTER_ANY ? undefined : (categoryFilter as ExpenseCategory),
    limit: pageSize,
    offset: (page - 1) * pageSize,
  });
  const { data, isLoading, isFetching, isError, refetch } = query;

  const total = data?.total ?? 0;
  const totalAmount = data?.total_amount_paise ?? 0;
  const allItems = React.useMemo(() => data?.items ?? [], [data]);

  const filteredItems = React.useMemo(() => {
    const needle = qInput.trim().toLowerCase();
    if (!needle) return allItems;
    return allItems.filter((e) =>
      [e.title, e.vendor_name ?? "", expenseCategoryLabel(e.category, e.custom_category_label)]
        .join(" ")
        .toLowerCase()
        .includes(needle),
    );
  }, [allItems, qInput]);

  const hasFilters = qInput !== "" || categoryFilter !== CATEGORY_FILTER_ANY;
  const clearFilters = () => {
    setQInput("");
    setCategoryFilter(CATEGORY_FILTER_ANY);
    setPage(1);
  };

  const handleDelete = async (e: Expense) => {
    try {
      await deleteMutation.mutateAsync(e.id);
      toast.success(`Deleted ${e.title}.`);
    } catch {
      toast.error("Couldn't delete the expense. Please retry.");
    }
  };

  return (
    <div className="space-y-4">
      {isError ? <LoadError onRetry={() => refetch()} label="expenses" /> : null}

      <DataTable>
        <DataTableToolbar>
          <div className="relative w-full sm:w-72">
            <Search
              aria-hidden="true"
              className="text-muted-foreground absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2"
            />
            <Input
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Search title or vendor…"
              className="pl-8"
              aria-label="Search expenses"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setPage(1);
            }}
            aria-label="Category filter"
            className="border-input bg-background h-9 rounded-md border px-2 text-sm"
          >
            <option value={CATEGORY_FILTER_ANY}>Any category</option>
            {(Object.keys(EXPENSE_CATEGORY_LABELS) as ExpenseCategory[]).map((c) => (
              <option key={c} value={c}>
                {EXPENSE_CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
          {hasFilters ? (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X aria-hidden="true" className="size-3.5" />
              Clear
            </Button>
          ) : null}
          <DataTableToolbarSpacer />
          <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw aria-hidden="true" className={cn("size-4", isFetching && "animate-spin")} />
            Refresh
          </Button>
          {canManage ? (
            <Button
              size="sm"
              onClick={() => setDialog({ kind: "create" })}
              data-testid="expense-add"
            >
              <ReceiptText aria-hidden="true" className="size-4" />
              Log expense
            </Button>
          ) : null}
        </DataTableToolbar>

        <DataTableScroll>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[30%]">Title</TableHead>
                <TableHead className="w-[16%]">Category</TableHead>
                <TableHead className="w-[16%]">Date</TableHead>
                <TableHead className="w-[16%]">Source</TableHead>
                <TableHead className="w-[16%] text-right">Amount</TableHead>
                <TableHead className="w-[6%] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            {isLoading ? (
              <DataTableSkeleton rows={5} columns={6} />
            ) : (
              <TableBody>
                {filteredItems.map((e) => (
                  <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                    <TableCell className="font-medium">
                      <div>{e.title}</div>
                      {e.vendor_name ? (
                        <div className="text-muted-foreground truncate text-xs">
                          {e.vendor_name}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">
                        {expenseCategoryLabel(e.category, e.custom_category_label)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs tabular-nums">
                      {new Date(e.incurred_on).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {e.recurring_expense_id
                        ? e.is_manual_override
                          ? "Adjusted month"
                          : "From schedule"
                        : "One-time"}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {formatPaise(e.amount_paise)}
                    </TableCell>
                    <TableCell className="text-right">
                      {canManage ? (
                        <RowActions
                          name={e.title}
                          onEdit={() => setDialog({ kind: "edit", expense: e })}
                          onDelete={() => handleDelete(e)}
                        />
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>

        {!isLoading && filteredItems.length === 0 ? (
          <DataTableEmpty
            icon={<ReceiptText aria-hidden="true" />}
            title={hasFilters ? "No matches" : "No expenses yet"}
            description={
              hasFilters
                ? "Adjust filters or clear them to see every expense."
                : "Log rent, electricity, supplies and other operating costs to see your net profit."
            }
            action={
              hasFilters ? (
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  <X aria-hidden="true" className="size-3.5" />
                  Clear filters
                </Button>
              ) : canManage ? (
                <Button size="sm" onClick={() => setDialog({ kind: "create" })}>
                  <ReceiptText aria-hidden="true" className="size-4" />
                  Log expense
                </Button>
              ) : null
            }
          />
        ) : null}

        {!isLoading && filteredItems.length > 0 ? (
          <DataTableFooter>
            <span className="text-muted-foreground text-sm tabular-nums">
              {total.toLocaleString()} expense{total === 1 ? "" : "s"} ·{" "}
              <span className="text-foreground font-medium">{formatPaise(totalAmount)}</span> total
            </span>
            <DataTablePagination
              className="ml-auto"
              page={page + 1}
              pageSize={pageSize}
              totalRows={total}
              isFetching={isFetching}
              onPageChange={(next) => setPage(Math.max(0, next - 1))}
              onPageSizeChange={(next) => {
                setPageSize(next);
                setPage(1);
              }}
              testIdPrefix="expenses-pagination"
            />
          </DataTableFooter>
        ) : null}
      </DataTable>

      {canManage && enabled ? (
        <ExpenseEditorSheet
          open={dialog.kind === "create" || dialog.kind === "edit"}
          onOpenChange={(next) => {
            if (!next) setDialog({ kind: "none" });
          }}
          existing={dialog.kind === "edit" ? dialog.expense : null}
          lockedMonth={dialog.kind === "create" ? lockedMonth : null}
          onSaved={() => setDialog({ kind: "none" })}
        />
      ) : null}
    </div>
  );
}

// ── Recurring costs ────────────────────────────────────────────────────

type RecurringDialog =
  | { kind: "none" }
  | { kind: "create" }
  | { kind: "edit"; expense: RecurringExpense }
  | { kind: "cancel"; expense: RecurringExpense };

const RECURRING_STATUS_FILTER = [
  { value: "__any__", label: "Any status" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "cancelled", label: "Cancelled" },
] as const;

function formatCalendarDay(iso: string | null): string {
  if (!iso) return "—";
  const [year, month, day] = iso.slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return iso;
  return new Date(year, month - 1, day).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function recurringRunLabel(row: RecurringExpense): string {
  if (row.occurrence_count) return `${row.occurrence_count} bills`;
  if (row.ends_on) return `Until ${formatCalendarDay(row.ends_on)}`;
  return "Ongoing";
}

function recurringPillStatus(status: RecurringExpenseStatus): "active" | "suspended" | "archived" {
  if (status === "paused") return "suspended";
  if (status === "cancelled") return "archived";
  return "active";
}

function todayDate(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

function RecurringTab({ canManage, enabled }: { canManage: boolean; enabled: boolean }) {
  const [qInput, setQInput] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState<string>("__any__");
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = usePageSizePreference({
    scope: "accounting-recurring",
    defaultPageSize: 10,
  });
  const [dialog, setDialog] = React.useState<RecurringDialog>({ kind: "none" });
  const action = useRecurringExpenseAction();

  const query = useRecurringExpenses({
    status: statusFilter === "__any__" ? undefined : (statusFilter as RecurringExpenseStatus),
    limit: pageSize,
    offset: (page - 1) * pageSize,
  });
  const { data, isLoading, isFetching, isError, refetch } = query;
  const total = data?.total ?? 0;
  const allItems = React.useMemo(() => data?.items ?? [], [data]);
  const filteredItems = React.useMemo(() => {
    const needle = qInput.trim().toLowerCase();
    if (!needle) return allItems;
    return allItems.filter((row) =>
      [
        row.title,
        row.vendor_name ?? "",
        expenseCategoryLabel(row.category, row.custom_category_label),
      ]
        .join(" ")
        .toLowerCase()
        .includes(needle),
    );
  }, [allItems, qInput]);
  const hasFilters = qInput !== "" || statusFilter !== "__any__";
  const clearFilters = () => {
    setQInput("");
    setStatusFilter("__any__");
    setPage(1);
  };

  const runAction = async (
    row: RecurringExpense,
    kind: "pause" | "resume" | "cancel" | "skip",
    on: string,
  ) => {
    try {
      await action.mutateAsync({ id: row.id, action: kind, on });
      const verb =
        kind === "pause"
          ? "Paused"
          : kind === "resume"
            ? "Resumed"
            : kind === "skip"
              ? "Skipped"
              : "Cancelled";
      toast.success(`${verb} ${row.title}.`);
      if (kind === "cancel") setDialog({ kind: "none" });
    } catch {
      toast.error("Couldn't update the recurring cost. Please retry.");
    }
  };

  return (
    <div className="space-y-4">
      {isError ? <LoadError onRetry={() => refetch()} label="recurring costs" /> : null}
      <DataTable>
        <DataTableToolbar>
          <div className="relative w-full sm:w-72">
            <Search
              aria-hidden="true"
              className="text-muted-foreground absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2"
            />
            <Input
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Search title or vendor…"
              className="pl-8"
              aria-label="Search recurring costs"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            aria-label="Status filter"
            className="border-input bg-background h-9 rounded-md border px-2 text-sm"
          >
            {RECURRING_STATUS_FILTER.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {hasFilters ? (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X aria-hidden="true" className="size-3.5" />
              Clear
            </Button>
          ) : null}
          <DataTableToolbarSpacer />
          <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw aria-hidden="true" className={cn("size-4", isFetching && "animate-spin")} />
            Refresh
          </Button>
          {canManage ? (
            <Button
              size="sm"
              onClick={() => setDialog({ kind: "create" })}
              data-testid="recurring-add"
            >
              <Repeat aria-hidden="true" className="size-4" />
              Add recurring cost
            </Button>
          ) : null}
        </DataTableToolbar>

        <DataTableScroll>
          <Table>
            <caption className="sr-only">Recurring operating costs</caption>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[24%]">Title</TableHead>
                <TableHead className="w-[16%]">Category</TableHead>
                <TableHead className="w-[14%]">Cadence</TableHead>
                <TableHead className="w-[16%]">Next bill</TableHead>
                <TableHead className="w-[12%]">Status</TableHead>
                <TableHead className="w-[12%] text-right">Amount</TableHead>
                <TableHead className="w-[6%] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            {isLoading ? (
              <DataTableSkeleton rows={5} columns={7} />
            ) : (
              <TableBody>
                {filteredItems.map((row) => (
                  <TableRow key={row.id} data-testid={`recurring-row-${row.id}`}>
                    <TableCell className="font-medium">
                      <div>{row.title}</div>
                      <div className="text-muted-foreground truncate text-xs">
                        {recurringRunLabel(row)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">
                        {expenseCategoryLabel(row.category, row.custom_category_label)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {RECURRING_CADENCE_LABELS[row.cadence]}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs tabular-nums">
                      {formatCalendarDay(row.next_occurrence_on)}
                    </TableCell>
                    <TableCell>
                      <StatusPill
                        status={recurringPillStatus(row.status)}
                        label={RECURRING_STATUS_LABELS[row.status]}
                      />
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {formatPaise(row.amount_paise)}
                    </TableCell>
                    <TableCell className="text-right">
                      {canManage && row.status !== "cancelled" ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Actions for ${row.title}`}
                              className="size-7"
                            >
                              <MoreHorizontal aria-hidden="true" className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem
                              onSelect={() => setDialog({ kind: "edit", expense: row })}
                            >
                              <Pencil aria-hidden="true" className="size-4" />
                              Edit
                            </DropdownMenuItem>
                            {row.status === "active" ? (
                              <DropdownMenuItem
                                onSelect={() => void runAction(row, "pause", todayDate())}
                              >
                                Pause
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onSelect={() => void runAction(row, "resume", todayDate())}
                              >
                                Resume
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              disabled={!row.next_occurrence_on}
                              onSelect={() => {
                                if (row.next_occurrence_on) {
                                  void runAction(row, "skip", row.next_occurrence_on);
                                }
                              }}
                            >
                              Skip next bill
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onSelect={() => setDialog({ kind: "cancel", expense: row })}
                            >
                              <Trash2 aria-hidden="true" className="size-4" />
                              Cancel schedule
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>

        {!isLoading && filteredItems.length === 0 ? (
          <DataTableEmpty
            icon={<Repeat aria-hidden="true" />}
            title={hasFilters ? "No matches" : "No recurring costs yet"}
            description={
              hasFilters
                ? "Adjust filters or clear them to see every schedule."
                : "Add water, electricity, rent, or any other cost that repeats so profit and loss stays accurate."
            }
            action={
              hasFilters ? (
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  <X aria-hidden="true" className="size-3.5" />
                  Clear filters
                </Button>
              ) : canManage ? (
                <Button size="sm" onClick={() => setDialog({ kind: "create" })}>
                  <Repeat aria-hidden="true" className="size-4" />
                  Add recurring cost
                </Button>
              ) : null
            }
          />
        ) : null}

        {!isLoading && filteredItems.length > 0 ? (
          <DataTableFooter>
            <span className="text-muted-foreground text-sm tabular-nums">
              {total.toLocaleString()} schedule{total === 1 ? "" : "s"}
            </span>
            <DataTablePagination
              className="ml-auto"
              page={page + 1}
              pageSize={pageSize}
              totalRows={total}
              isFetching={isFetching}
              onPageChange={(next) => setPage(Math.max(0, next - 1))}
              onPageSizeChange={(next) => {
                setPageSize(next);
                setPage(1);
              }}
              testIdPrefix="recurring-pagination"
            />
          </DataTableFooter>
        ) : null}
      </DataTable>

      {dialog.kind === "cancel" ? (
        <div
          role="alertdialog"
          aria-labelledby="cancel-recurring-title"
          className="border-destructive/30 bg-card flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <div>
            <p id="cancel-recurring-title" className="text-sm font-medium">
              Cancel {dialog.expense.title}?
            </p>
            <p className="text-muted-foreground text-xs">
              Bills already posted stay in this month&apos;s profit and loss. No further bills are
              created.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setDialog({ kind: "none" })}>
              Keep it
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={action.isPending}
              onClick={() => void runAction(dialog.expense, "cancel", todayDate())}
            >
              Cancel schedule
            </Button>
          </div>
        </div>
      ) : null}

      {canManage && enabled ? (
        <RecurringExpenseEditorSheet
          open={dialog.kind === "create" || dialog.kind === "edit"}
          onOpenChange={(next) => {
            if (!next) setDialog({ kind: "none" });
          }}
          existing={dialog.kind === "edit" ? dialog.expense : null}
        />
      ) : null}
    </div>
  );
}

// ── Team cost tab ──────────────────────────────────────────────────────

/**
 * Phase SC-22d — staff costs moved to Team pay (attendance-driven pay,
 * advances, payments). Accounting keeps the cost picture and links out.
 */
function TeamCostTab({ year, month, enabled }: { year: number; month: number; enabled: boolean }) {
  const { vertical, isModuleEnabled } = useEnabledModules();
  const access = useTeamPayAccess();
  const isStudyCentre = vertical === "study_centre";
  const teamNoun = isStudyCentre ? "team" : "staff";
  const payroll = isModuleEnabled("payroll");
  const payees = useStaffPayees({ is_active: true, limit: 1, offset: 0 });
  const summary = useTeamPaySummary(year, month, enabled && payroll && access.canView);
  const totals = summary.data?.totals;

  return (
    <div className="space-y-4">
      {payees.isError ? (
        <LoadError onRetry={() => payees.refetch()} label={`${teamNoun} costs`} />
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Monthly pay on record"
          value={payees.isLoading ? "—" : formatPaise(payees.data?.active_monthly_cost_paise ?? 0)}
          caption={`${payees.data?.total ?? 0} active ${isStudyCentre ? "team members" : "staff"}`}
          icon={<Users />}
          tone="blue"
        />
        {totals ? (
          <>
            <StatCard
              label="Earned this month"
              value={formatPaise(totals.earned_paise + totals.additions_paise)}
              caption={monthLabel(year, month)}
              icon={<Wallet />}
              tone="violet"
            />
            <StatCard
              label="Paid this month"
              value={formatPaise(totals.paid_paise)}
              caption="Salary and dues"
              icon={<Banknote />}
              tone="green"
            />
            <StatCard
              label="Owed today"
              value={formatPaise(Math.max(0, totals.owed_today_paise))}
              caption="Still to pay"
              icon={<Clock />}
              tone="amber"
            />
          </>
        ) : null}
      </div>
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-base">
              {isStudyCentre ? "Team pay" : "Staff pay"} has its own page
            </CardTitle>
            <CardDescription>
              Attendance, pay terms, advances and payments live there. Finalized pay runs become the{" "}
              {teamNoun} cost line in your profit &amp; loss.
            </CardDescription>
          </div>
          {payroll ? (
            <Button asChild size="sm">
              <Link to="/team-pay">
                Open {isStudyCentre ? "Team pay" : "Staff pay"}
                <ArrowRight aria-hidden="true" className="size-4" />
              </Link>
            </Button>
          ) : null}
        </CardHeader>
      </Card>
    </div>
  );
}

// ── Shared bits ────────────────────────────────────────────────────────

function RowActions({
  name,
  onEdit,
  onDelete,
}: {
  name: string;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={`Actions for ${name}`} className="size-7">
          <MoreHorizontal aria-hidden="true" className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem onSelect={onEdit}>
          <Pencil aria-hidden="true" className="size-4" />
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onDelete} className="text-destructive focus:text-destructive">
          <Trash2 aria-hidden="true" className="size-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function LoadError({ onRetry, label }: { onRetry: () => void; label: string }) {
  return (
    <div
      role="alert"
      className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
    >
      <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
      <span className="flex-1">Couldn&apos;t load {label}.</span>
      <Button variant="ghost" size="sm" className="h-auto px-2 py-0.5 text-xs" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}
